import type { EmployerSiteNames } from '@seek/hirer-graphql-client/lib-types/constants/headers';
import {
  type Language,
  type Locale,
  getLanguageFromLocale,
  siteNames,
} from '@seek/melways-sites';
import get from 'lodash/get';

import { Environment } from './types';

enum ConfigItemKey {
  VERSION = 'version',
  ENV = 'environment',
  SITE = 'site',
  LOCALE = 'locale',
  LANGUAGE = 'language',
}

export interface Config {
  [ConfigItemKey.VERSION]: string;
  [ConfigItemKey.ENV]: Environment;
  [ConfigItemKey.SITE]: EmployerSiteNames;
  [ConfigItemKey.LOCALE]: Locale;
  [ConfigItemKey.LANGUAGE]: Language;
}

declare global {
  interface Window {
    __config: Config;
  }
}

interface ConfigOptions {
  environment: Environment;
  appVersion: string;
  site: EmployerSiteNames;
  locale: Locale;
}

const DEFAULT_SITE = siteNames.employer.seek.au;
const DEFAULT_LOCALE = 'en-AU';

export const createConfig = ({
  environment,
  appVersion,
  site,
  locale,
}: ConfigOptions) => ({
  [ConfigItemKey.VERSION]: appVersion,
  [ConfigItemKey.ENV]: environment,
  [ConfigItemKey.SITE]: site,
  [ConfigItemKey.LOCALE]: locale,
  [ConfigItemKey.LANGUAGE]: getLanguageFromLocale(locale),
});

type ConfigItemFallback = any;

const getConfigItem = (
  configItem: ConfigItemKey,
  fallback: ConfigItemFallback,
) => {
  if (typeof window !== 'undefined' && window.__config) {
    return get(window.__config, configItem, fallback);
  }
  return fallback;
};

export const getEnvironment = (): Environment =>
  getConfigItem(ConfigItemKey.ENV, Environment.DEV);

export const getSite = (): EmployerSiteNames =>
  getConfigItem(ConfigItemKey.SITE, DEFAULT_SITE);

export const getLocale = (): Locale =>
  getConfigItem(ConfigItemKey.LOCALE, DEFAULT_LOCALE);

export const getLanguage = (): Language =>
  getConfigItem(ConfigItemKey.LANGUAGE, 'en');

export const getVersion = (): Language =>
  getConfigItem(ConfigItemKey.VERSION, '0.0.0');
